import { Component, ElementRef, Injector } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  BaseResourceFormComponent, FuncaoService, GlobalService, LoginContabil, RequisicaoItemMovimento, RequisicaoMovimento, SetorAlmoxarifado,
  SetorAlmoxarifadoService, StatusRequisicao
} from 'eddydata-lib';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';
import { ProdutoUnidadeService } from '../../produto/service/produto-unidade.service';
import { ProdutoService } from '../../produto/service/produto.service';
import { RequisicaoMovimentoService } from '../service/requisicao-movimento.service';

@Component({
  selector: 'lib-requisicao-form',
  templateUrl: './requisicao-form.component.html'
})
export class RequisicaoFormComponent extends BaseResourceFormComponent<RequisicaoMovimento, LoginContabil> {
  /**
   * Declaração de variáveis
   */
  public listStatus: StatusRequisicao[] = [];
  public listSetores: SetorAlmoxarifado[];
  public itens: RequisicaoItemMovimento[];

  public visualizarEstorno: boolean = false;
  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    public globalService: GlobalService,
    public funcaoService: FuncaoService,
    protected produtoService: ProdutoService,
    protected produtoUnService: ProdutoUnidadeService,
    private setorAlmoxarifadoService: SetorAlmoxarifadoService,
    protected requisicaoMovService: RequisicaoMovimentoService) {
    super(new RequisicaoMovimento(), injector, RequisicaoMovimento.converteJson, requisicaoMovService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      data_cadastro: [new Date()],
      status: [StatusRequisicao.AGUARDANDO, Validators.required],
      destinatario: [null],
      motivo_devolucao: [null],
      devolucao: [false, Validators.required],
      setor_almoxarifado: [null, Validators.required],
      usuario: [this.login.usuario],
      exercicio: [this.login.exercicio],
      orgao: [this.login.orgao],
      itens: [null]
    });
  }

  protected parametrosExtras(): {} {
    return {
      relations: [
        'usuario', 'setor_almoxarifado', 'setor_almoxarifado.setor', 'setor_almoxarifado.estoque',
        'exercicio', 'orgao', 'usuario', 'recebedor', 'itens', 'itens.produto_unidade',
        'itens.produto_unidade.produto', 'itens.produto_unidade.unidade', 'itens.produto_unidade_troca',
        'itens.produto_unidade_troca.produto', 'itens.produto_unidade_troca.unidade'
      ]
    };
  }

  protected afterInit(): void {
    this.listStatus = this.globalService.statusRequisicao();
    this.carregarSetores();

    this.entidade.data_cadastro = new Date();
    this.entidade.usuario = this.login.usuario;
    this.entidade.status = StatusRequisicao.AGUARDANDO;
  }

  protected campoFoco(): ElementRef {
    return null;
  }

  protected async afterLoad() {
    this.itens = this.entidade.itens;
    if (this.entidade.id)
      this.entidadeForm.disable();
  }
  protected beforeSubmit() {
    this.entidadeForm.get('itens').setValue(this.itens);
    console.log(this.entidadeForm.value);
  }

  protected afterSubmit(ent: RequisicaoMovimento) {
    this.entidadeForm.get('itens').setValue(ent.itens);
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  private carregarSetores() {
    this.setorAlmoxarifadoService.filtrar(0, 0, {
      estoque_id: this.login.usuario.estoque.id, relations: ['setor', 'estoque']
    }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listSetores = res ? res.content : [];
        if (this.listSetores.length === 0) {
          toastr.error('Nenhum setor foi localizado neste almoxarifado!');
          this.sair();
          return;
        }
        this.entidadeForm.get('setor_almoxarifado').setValue(null);
        for (const s of res.content as SetorAlmoxarifado[]) {
          if (s.padrao) {
            this.entidadeForm.get('setor_almoxarifado').setValue(s);
            break;
          }
        }
      }, error => toastr.error('Nenhum setor foi localizado neste almoxarifado!'));
  }

  public importarRequisicao(requisicao: RequisicaoMovimento) {
    try {
      if (!this.entidade.itens)
        this.entidade.itens = [];
      for (let item of requisicao.itens) {
        for (let item2 of this.entidade.itens) {
          if (item.produto_unidade.id === item2.produto_unidade.id)
            throw new Error(`${item.produto_unidade.produto.nome} ja foi adicionado aos itens!`);
        }
      }
      this.entidade.itens = this.entidade.itens.concat(requisicao.itens.map((i) => {
        let item = new RequisicaoItemMovimento();
        item.produto_unidade = i.produto_unidade;
        item.qtd_requisitada = i.qtd_requisitada;
        item.status = StatusRequisicao.AGUARDANDO;
        return item;
      }));
      this.afterLoad();
    } catch (err) {
      toastr.warning(err.message);
    }
  }

  public disabledItem() {
    return this.itens && this.itens.length > 0;
  }

  public tituloStatusRequisicao(status: StatusRequisicao): string {
    return this.globalService.tituloStatusRequisicao(status);
  }

  public iconStatusRequisicao(status: StatusRequisicao): {} {
    let icon = this.globalService.iconStatusRequisicao(status);
    let ngIcon = {};
    ngIcon[icon] = true;
    return ngIcon;
  }

  public corStatusRequisicao(status: StatusRequisicao): {} {
    let cor = this.globalService.corStatusRequisicao(status);
    let ngCor = {};
    ngCor[`bg-${cor}`] = true;
    return ngCor;
  }

  public badgeStatusRequisicao(status: StatusRequisicao): {} {
    let cor = this.globalService.corStatusRequisicao(status);
    let ngCor = {};
    ngCor[`badge-${cor}`] = true;
    return ngCor;
  }

  public valorRequisitado() {
    if (!this.itens)
      return 0.0;
    let vl = 0.0;
    for (let it of this.itens) {
      if (+it.qtd_requisitada)
        vl += Number.parseFloat(it.qtd_requisitada.toString());
    }
    return vl;
  }

  public valorAceito() {
    if (!this.itens)
      return 0.0;
    let vl = 0.0;
    for (let it of this.itens) {
      if (it.qtd_aceita)
        vl += Number.parseFloat(it.qtd_aceita.toString());
    }
    return vl;
  }

  public valorRecusado() {
    if (!this.itens)
      return 0.0;
    let vl = 0.0;
    for (let it of this.itens) {
      if (it.status && it.status !== StatusRequisicao.AGUARDANDO)
        vl += Number.parseFloat(it.qtd_requisitada.toString()) - Number.parseFloat(it.qtd_aceita.toString());
    }
    return vl;
  }

}
